/**
 * This file includes colors that map to the .Colors Figma collection
 * and represent the legacy color palette.
 */

export type TColorKeys = keyof typeof colors;
export type TColorValues = (typeof colors)[TColorKeys];

const base = {
    alpine: '#11768d',
    alpineDark: '#0e5f72',
    alpine_01: '#e7f1f4',
    alpine700: '#0B4856',
    black: '#000000',
    black_005: 'rgba(0, 0, 0, 0.05)',
    black_01: 'rgba(0, 0, 0, 0.1)',
    black_02: 'rgba(0, 0, 0, 0.2)',
    black_03: 'rgba(0, 0, 0, 0.3)',
    black_04: 'rgba(0, 0, 0, 0.4)',
    black_05: 'rgba(0, 0, 0, 0.5)',
    black_07: 'rgba(0, 0, 0, 0.7)',
    black_08: 'rgba(0, 0, 0, 0.8)',
    blue10: '#a6b8f3',
    blue10_10_hex: '#eff2ff',
    blue20: '#597eff',
    blue20_08: 'rgba(89, 126, 255, 0.08)',
    blue20_10: 'rgba(89, 126, 255, 0.1)',
    blue20_10_hex: '#EEF2FF',
    blue20_12: 'rgba(89, 126, 255, 0.12)',
    blue20_15: 'rgba(89, 126, 255, 0.15)',
    blue20_20: 'rgba(89, 126, 255, 0.2)',
    blue20_20_hex: '#e6ecff',
    blue20_30: 'rgba(89, 126, 255, 0.3)',
    blue20_40: 'rgba(89, 126, 255, 0.4)',
    blue20_50: 'rgba(89, 126, 255, 0.5)',
    blue20_5: 'rgba(89, 126, 255, 0.05)',
    blue20_60: 'rgba(89, 126, 255, 0.6)',
    blue25: '#3C5CCB',
    blue30: '#283f88',
    blue40: '#0c1f5c',
    blue50: '#001247',
    blue50_20: 'rgba(0, 18, 71, 0.2)',
    blue75: '#D9E2FF',
    blue90: '#000021',
    blue_006: 'rgba(0, 18, 71, 0.06)',
    blue_008: 'rgba(0, 18, 71, 0.08)',
    blue_015: 'rgba(0, 18, 71, 0.15)',
    blue_01: 'rgba(0, 18, 71, 0.1)',
    blue_03: 'rgba(0, 18, 71, 0.3)',
    contextualBlue: 'rgba(0, 18, 71, 0.9)',
    defaultBrandColor: '#007DB3',
    facebook: '#1877F2',
    flame500: '#E0673A',
    geyserLight: '#f1f4f6',
    geyserLightElevation: '#c7d3d6',
    geyser500: '#c7d5d9',
    google: '#df4a32',
    green40: '#6ee59a',
    green40_08: 'rgba(110, 229, 154, 0.08)',
    green50: '#4cc676',
    green60: '#249b4d',
    green100: '#EEFCF3',
    green600: '#319B56',
    green700: '#107934',
    instagram: '#d93175',
    lavender500: '#8097F3',
    lavender700: '#445EC5',
    linkedin: '#007bb6',
    merino500: '#f4eae0',
    merinoLight: '#f9f5ef',
    merinoLightElevation: '#e7dbd0',
    orange50: '#fbb16b',
    orange60: '#e17c00',
    orange200: 'rgba(255, 237, 214, 1)',
    orange700: 'rgba(163, 90, 0, 1)',
    orange500: 'rgba(225, 124, 0, 1)',
    outline: '#2684ff',
    pink500: '#F6D1FF',
    pinterest: '#cb2027',
    purple50: '#9271f5',
    purple50_01: 'rgba(146, 113, 245, 0.1)',
    purple100: '#F8F6FE',
    purple200: '#F2EEFD',
    purple300: '#D9CDF9',
    purple400: '#A88DF2',
    purple500: '#5E30E3',
    purple600: '#4522AF',
    purple700: '#341A85',
    purple800: '#2C1576',
    purpleHeart: '#5e30e3',
    purple_20: '#F2EEFD',
    rebelYellow: '#ffd552',
    red50: '#ff6161',
    red50_01: 'rgba(255, 97, 97, 0.1)',
    red50_08: 'rgba(255, 97, 97, 0.08)',
    red50_15: 'rgba(255, 97, 97, 0.15)',
    red50_35: 'rgba(255, 97, 97, 0.35)',
    red50_60: 'rgba(255, 97, 97, 0.6)',
    red60: '#d73333',
    red100: '#FFF0F0',
    red200: '#FFD6D6',
    red500: '#D73333',
    red600: '#BC2525',
    red700: '#A22020',
    rosemary500: '#659675',
    skyblue50: '#59ccff',
    transparent: 'transparent',
    transparent_black: '#333333',
    twitter: '#00b6f1',
    violet50: '#7492fd',
    warningRed100: '#fff0f0',
    white: '#ffffff',
    white_02: 'rgba(255, 255, 255, 0.2)',
    white_03: 'rgba(255, 255, 255, 0.3)',
    white_04: 'rgba(255, 255, 255, 0.4)',
    white_065: 'rgba(255, 255, 255, 0.65)',
    white_085: 'rgba(255, 255, 255, 0.85)',
    yellow50: '#ffdd0f',
    yellow60: '#e6bd00',
    yellow500: '#FFD552',
    yellow600: '#F5C738',
    yellow700: '#EDBA1D'
} as const;

const neutral = {
    neutral100: '#F6F7F8',
    neutral200: '#EEEFF1',
    neutral300: '#E6E7EA',
    neutral400: '#D8DADE',
    neutral500: '#B2B5BD',
    neutral600: '#868A98',
    neutral700: '#717684',
    neutral800: '#525660',
    neutral900_light: '#232529',
    neutral900: '#131416'
} as const;

const accent = {
    accent100: '#F2F5FF',
    accent500: base.blue20
} as const;

/** @deprecated - use theme CSS vars instead */
const danger = {
    danger100: base.red100,
    danger500: base.red500,
    danger700: base.red700
} as const;

/** @deprecated - use theme CSS vars instead */
const text = {
    textPrimary: neutral.neutral900,
    textSecondary: neutral.neutral800,
    textTertiary: neutral.neutral700,
    textDisabled: neutral.neutral500,
    textWhite: base.white,
    textBrand: base.blue25
} as const;

/** @deprecated - use theme CSS vars instead */
const surface = {
    surfaceItem: base.white,
    surfaceItemHover: neutral.neutral100,
    surfaceItemSelected: neutral.neutral200,
    surfaceItemSelectedHover: neutral.neutral300,
    surfaceDisabled: neutral.neutral200,
    surfaceBrand: accent.accent100,
    surfaceBrandHovered: base.blue20_20_hex,
    surfacePressed: neutral.neutral200,
    surfaceSelectedPressed: neutral.neutral400,
    surfaceBrandPressed: base.blue75
} as const;

/** @deprecated - use theme CSS vars instead */
const stroke = {
    strokePrimary: neutral.neutral500,
    strokeSecondary: neutral.neutral300,
    strokeTertiary: neutral.neutral100,
    strokeDisabled: neutral.neutral300,
    strokeSelected: neutral.neutral900,
    strokeSelectedDisabled: neutral.neutral500
} as const;

/** @deprecated - use theme CSS vars instead */
const brand = {
    brandPrimary: accent.accent500
} as const;

const ai = {
    ai_purple: base.purple500,
    ai_lavender: base.lavender500,
    ai_pink: base.pink500,
    ai_yellow: base.rebelYellow,
    ai_amaranth: '#B952F6'
} as const;

const brandColors = {
    lilac: '#DD99ED',
    matcha: '#99D3AC',
    ocean: '#9AB9E8',
    wildflower: '#FFFA77',
    ube: '#C3B0F8',
    honey: '#FFFA56',
    gold: '#FFDA00',
    keyLime: '#DBF0A1',
    salmon: '#FF99A7',
    lavender: '#DBD2F7',
    sky: '#A8D0EF'
};

export { brandColors };

const colors = {
    ...base,
    ...neutral,
    ...accent,
    ...danger,
    ...text,
    ...ai,
    ...surface,
    ...stroke,
    ...brand,
    ...brandColors
} as const;

export default colors;
